import React, { useMemo } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import classes from '../../bidAnalysisTemplate.module.scss';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ColType = 'currency' | 'percent' | 'str' | 'int' | 'num';

interface NCColumn {
  key: string;
  label: string;
  type?: ColType;
}

export interface NCGlobalOutputTableProps {
  /** yearFormData[packIndex][yearIndex] — persisted per-year data */
  yearFormData: Record<number, Record<number, any>>;
  /** Base pack data (conceptName, packSize, …) keyed by packIndex */
  packSizeFormData: Record<number, any>;
  /** Live form data for the currently active pack + year (updated on every keystroke) */
  formData: any;
  activePackSizeTab: number | null;
  activeYearTab: number | null;
  allPackSizeTabs: Array<{
    index: number;
    conceptName?: string;
    packSize?: string;
    label?: string;
  }>;
  bidApprovedBy?: string | null;
  /** Global Project Information field — applies to every row regardless of active pack. */
  gsmsHndlgType?: string;
  isEditMode?: boolean;
  onFieldChange?: (field: string, value: any) => void;
  onFieldBlur?: (field: string, event?: any) => void;
  onFieldKeyDown?: (field: string, e: React.KeyboardEvent) => void;
  onSetActivePackSizeTab?: (tabIndex: number) => void;
  onSetActiveYearTab?: (yearIndex: number) => void;
  copyForAllYears?: () => void;
}

// ---------------------------------------------------------------------------
// Column definitions — duplicate gsmsPackSize removed (kept first occurrence)
// ---------------------------------------------------------------------------

const NC_COLUMNS: NCColumn[] = [
  { key: 'conceptName', label: 'DESCRIPTION', type: 'str' },
  { key: 'mfgBottleSize', label: 'MFG BOTTLE SIZE', type: 'int' },
  { key: 'gsmsPackSize', label: 'GSMS PACK SIZE', type: 'int' },
  { key: 'gsms_mfg_ndc', label: 'SUPPLIER BULK NDC', type: 'str' },
  { key: 'vaAnnualNcEst', label: 'VA ANNUAL NC EST', type: 'num' },
  { key: 'dodAnnualNcEst', label: 'DOD ANNUAL NC EST', type: 'num' },
  { key: 'ihsAnnualNcEst', label: 'IHS ANNUAL NC EST', type: 'num' },
  { key: 'bopAnnualNcEst', label: 'BOP ANNUAL NC EST', type: 'num' },
  { key: 'ncEstAnnualUsage', label: 'NC EST ANNUAL USAGE', type: 'num' },
  { key: 'gsmsCostFromMfg', label: 'GSMS COST FROM MFG', type: 'currency' },
  { key: 'gsmsNetCostFromMfg', label: 'GSMS NET COST FROM MFG', type: 'currency' },
  { key: 'gsmsHndlgType', label: 'GSMS HNDLG TYPE', type: 'str' },
  { key: 'gsmsBottleCapacity', label: 'GSMS BOTTLE CAPACITY', type: 'str' },
  { key: 'gsmsBottleCost', label: 'GSMS BOTTLE COST', type: 'currency' },
  { key: 'gsmsCapCost', label: 'GSMS CAP COST', type: 'currency' },
  { key: 'gsmsLabelCost', label: 'GSMS LABEL COST', type: 'currency' },
  { key: 'gsmsInsertCost', label: 'GSMS INSERT COST', type: 'currency' },
  { key: 'gsmsIffFee', label: 'GSMS IFF FEE', type: 'currency' },
  { key: 'gsmsBoxCartonCost', label: 'GSMS BOX/CARTON COST', type: 'currency' },
  { key: 'gsmsDesiccantCost', label: 'GSMS DESICCANT COST', type: 'currency' },
  { key: 'outsourceRepackFee', label: 'OUTSOURCE REPACK FEE', type: 'currency' },
  { key: 'blendedWhlsFee', label: 'BLENDED WHLS FEE', type: 'currency' },
  { key: 'gsmsFrtCost', label: 'GSMS FRT. COST', type: 'currency' },
  { key: 'pv2Percent', label: 'GSMS PV 2%', type: 'currency' },
  { key: 'gsmsLabor', label: 'GSMS LABOR', type: 'currency' },
  { key: 'gsmsCost', label: 'GSMS COST', type: 'currency' },
  { key: 'suggestedBidPrice', label: 'SUGGESTED BID PRICE', type: 'currency' },
  { key: 'wac', label: 'WAC', type: 'currency' },
  { key: 'suggestedGsmsWac', label: 'SUPPLIER COMPARABLE WAC', type: 'currency' },
  { key: 'approvedBy', label: 'APPROVED BY', type: 'str' },
  { key: 'gsmsProfit', label: 'GSMS PROFIT', type: 'percent' },
  { key: 'gsmsMarketingFee', label: 'GSMS MARKETING FEE', type: 'currency' },
  { key: 'contractAnnualRevenue', label: 'CONTRACT ANNUAL REVENUE', type: 'currency' },
  { key: 'contractAnnualGP', label: 'CONTRACT ANNUAL GP', type: 'currency' },
  { key: 'contractAnnualCost', label: 'CONTRACT ANNUAL COST', type: 'currency' },
  { key: 'govSavingsAnalysis', label: 'GOVT SAVINGS ANALYSIS', type: 'currency' },
];

/** Columns that are editable in NC edit mode */
const NC_EDITABLE_COLS = new Set([
  'vaAnnualNcEst',
  'dodAnnualNcEst',
  'ihsAnnualNcEst',
  'bopAnnualNcEst',
  'gsmsCostFromMfg',
  'mfgBottleSize',
  'gsmsDesiccantCost',
  'suggestedBidPrice',
  'wac',
  'suggestedGsmsWac',
  'gsmsProfit',
]);

/** Some column keys differ from the handler field names */
const COL_KEY_TO_HANDLER_FIELD: Record<string, string> = {
  suggestedGsmsWac: 'supplier_comparable_wac',
};

/** Columns whose text should never wrap */
const NOWRAP_COLS = new Set(['conceptName', 'gsms_mfg_ndc', 'gsms_ndc']);

const YEAR_LABELS = [
  'Base Year',
  'Option Year 1',
  'Option Year 2',
  'Option Year 3',
  'Option Year 4',
];

/** Columns included in the per-year totals row */
const TOTAL_KEYS = new Set([
  'ncEstAnnualUsage',
  'contractAnnualRevenue',
  'contractAnnualGP',
  'contractAnnualCost',
  'govSavingsAnalysis',
]);

// ---------------------------------------------------------------------------
// Formatting helpers (same approach as GlobalOutputTable)
// ---------------------------------------------------------------------------

const formatCurrency = (value: any): string => {
  if (value === null || value === undefined || value === '') return '$0.00';
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  if (isNaN(num)) return '$0.00';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// For fields the form shows with up to 3 decimal places (CogsBuildupSection maxFractionDigits=3)
const formatCurrency3 = (value: any): string => {
  const num = parseFloat(String(value));
  if (value === null || value === undefined || value === '' || isNaN(num)) return '$0.000';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
};

// For fields the form shows with up to 5 decimal places (CogsBuildupSection maxFractionDigits=5)
const formatCurrency5 = (value: any): string => {
  const num = parseFloat(String(value));
  if (value === null || value === undefined || value === '' || isNaN(num)) return '$0.00000';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 5, maximumFractionDigits: 5 })}`;
};

const formatNum = (value: any): string => {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatInt = (value: any): string => {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  if (isNaN(num)) return String(value) || '—';
  return String(Math.round(num));
};

const formatPercent = (value: any): string => {
  if (value === null || value === undefined || value === '') return '0.00%';
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  if (isNaN(num)) return '0.00%';
  return `${num.toFixed(2)}%`;
};

const formatStr = (value: any): string => {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

// ---------------------------------------------------------------------------
// Cell-value resolver
// ---------------------------------------------------------------------------

function getCellValue(
  packData: any,
  col: NCColumn,
  bidApprovedBy?: string | null,
  hndlgType?: string
): string {
  if (col.key === 'conceptName') return formatStr(packData.conceptName);
  if (col.key === 'wac') {
    const wac = packData.wac ?? packData.supplier_wac;
    if (wac === null || wac === undefined || wac === '') return '—';
    const n = typeof wac === 'number' ? wac : parseFloat(String(wac));
    return isNaN(n) ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (col.key === 'suggestedGsmsWac') {
    return formatCurrency(packData.suggestedGsmsWac ?? packData.supplier_comparable_wac);
  }
  if (col.key === 'approvedBy') {
    const v = bidApprovedBy ?? packData.approvedBy;
    return v == null || v === '' ? '—' : String(v);
  }
  if (col.key === 'gsms_mfg_ndc') {
    return formatStr(packData.selectedMfgNdc || packData.mfg_ndc || packData.gsms_mfg_ndc);
  }
  if (col.key === 'mfgBottleSize') return formatInt(packData.mfgBottleSize);
  if (col.key === 'gsmsPackSize') return formatInt(packData.gsmsPackSize ?? packData.packSize);
  if (col.key === 'gsmsHndlgType') return formatStr(hndlgType || packData.gsmsHndlgType);
  if (col.key === 'gsmsBottleCapacity') return formatStr(packData.gsmsBottleCapacity);

  // Fields displayed at 3dp in CogsBuildupSection (maxFractionDigits=3)
  if (
    col.key === 'gsmsBottleCost' ||
    col.key === 'gsmsCapCost' ||
    col.key === 'gsmsLabelCost' ||
    col.key === 'gsmsInsertCost' ||
    col.key === 'gsmsBoxCartonCost'
  ) {
    return formatCurrency3(packData[col.key]);
  }

  // Fields displayed at 5dp in CogsBuildupSection (maxFractionDigits=5)
  if (col.key === 'gsmsFrtCost' || col.key === 'gsmsLabor') {
    return formatCurrency5(packData[col.key]);
  }

  const raw = packData[col.key];
  switch (col.type) {
    case 'currency':
      return formatCurrency(raw);
    case 'percent':
      return formatPercent(raw);
    case 'int':
      return formatInt(raw);
    case 'str':
      return formatStr(raw);
    case 'num':
    default:
      return formatNum(raw);
  }
}

// ---------------------------------------------------------------------------
// Raw-value extractor (for InputNumber initial value)
// ---------------------------------------------------------------------------

function getRawValue(packData: any, col: NCColumn): number | null {
  let v: any;
  if (col.key === 'suggestedGsmsWac')
    v = packData.suggestedGsmsWac ?? packData.supplier_comparable_wac;
  else if (col.key === 'wac') v = packData.wac ?? packData.supplier_wac;
  else if (col.key === 'mfgBottleSize') v = packData.mfgBottleSize;
  else v = packData[col.key];
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(String(v));
  return isNaN(n) ? null : n;
}

// ---------------------------------------------------------------------------
// th style helper
// ---------------------------------------------------------------------------

function getThStyle(colKey: string): React.CSSProperties {
  if (colKey === 'conceptName') return { minWidth: '180px', whiteSpace: 'nowrap' };
  if (NOWRAP_COLS.has(colKey)) return { whiteSpace: 'nowrap', minWidth: '100px' };
  return {
    whiteSpace: 'normal',
    wordBreak: 'keep-all',
    overflowWrap: 'normal',
    minWidth: '90px',
    maxWidth: '130px',
  };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const NCGlobalOutputTable: React.FC<NCGlobalOutputTableProps> = ({
  yearFormData,
  packSizeFormData,
  formData,
  activePackSizeTab,
  activeYearTab,
  allPackSizeTabs,
  bidApprovedBy,
  gsmsHndlgType,
  isEditMode,
  onFieldChange,
  onFieldBlur,
  onFieldKeyDown,
  onSetActivePackSizeTab,
  onSetActiveYearTab,
  copyForAllYears,
}) => {
  /**
   * Global Project Information fields — same value for every pack size / year.
   * Extracted from formData (which always carries them via preserveGlobalFields)
   * and used as a base layer so all rows show the correct global values even when
   * packSizeFormData entries don't have them stored.
   */
  const globalFields = useMemo(() => {
    const GLOBAL_KEYS = [
      'gsmsHndlgType',
      'supplierName',
      'analysisDate',
      'targetLaunchDate',
      'analysisType',
      'contracts',
      'contractsData',
      'productName',
      'analystName',
    ];
    const result: Record<string, any> = {};
    GLOBAL_KEYS.forEach(k => {
      if (formData[k] !== undefined && formData[k] !== null && formData[k] !== '') {
        result[k] = formData[k];
      }
    });
    return result;
  }, [formData]);

  /**
   * Build a flat list of row objects for every (yearIndex, packTab) pair.
   * Each row carries `_packKey` and `_yearIndex` so the editable cell
   * can call onSetActiveYearTab / onSetActivePackSizeTab on focus.
   *
   * Merge order (lowest → highest priority):
   *   globalFields  ← Project Info applies to every row as a baseline
   *   baseData      ← packSizeFormData (per-pack, overrides global defaults)
   *   yearData      ← yearFormData (per-year, overrides base)
   *   formData      ← live active data (only for the active pack+year)
   */
  const rowsByYear = useMemo(() => {
    return YEAR_LABELS.map((_label, yearIndex) => {
      const rows = allPackSizeTabs.map(packTab => {
        const baseData = packSizeFormData[packTab.index] || {};
        const yearData = yearFormData[packTab.index]?.[yearIndex] || {};
        const isActive = activePackSizeTab === packTab.index && activeYearTab === yearIndex;
        const merged = isActive
          ? { ...globalFields, ...baseData, ...yearData, ...formData }
          : { ...globalFields, ...baseData, ...yearData };
        return { ...merged, _packKey: packTab.index, _yearIndex: yearIndex };
      });
      return rows;
    });
  }, [
    allPackSizeTabs,
    yearFormData,
    packSizeFormData,
    formData,
    globalFields,
    activePackSizeTab,
    activeYearTab,
  ]);

  if (allPackSizeTabs.length === 0) {
    return (
      <div className={classes.globalTableWrapper}>
        <p>No data available</p>
      </div>
    );
  }

  const colCount = NC_COLUMNS.length;

  return (
    <div className={classes.globalTableWrapper}>
      <table className={classes.globalTable}>
        <thead>
          <tr>
            {NC_COLUMNS.map(c => (
              <th key={c.key} style={getThStyle(c.key)}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {YEAR_LABELS.map((yearLabel, yearIndex) => {
            const yearRows = rowsByYear[yearIndex];

            // Per-year totals: sum TOTAL_KEYS columns across all pack rows
            const yearTotals: Record<string, number> = {};
            TOTAL_KEYS.forEach(k => {
              yearTotals[k] = 0;
            });
            yearRows.forEach(row => {
              TOTAL_KEYS.forEach(k => {
                const v = parseFloat(row[k] || '0');
                if (!isNaN(v)) yearTotals[k] += v;
              });
            });

            return (
              <React.Fragment key={yearIndex}>
                {/* Year section header row */}
                {yearIndex === 0 ? (
                  <tr
                    style={{
                      background: 'linear-gradient(90deg, #ede4f7 0%, #f5f0fb 100%)',
                      borderTop: '2px solid #c4a8e0',
                    }}
                  >
                    <td
                      colSpan={colCount}
                      style={{
                        padding: '7px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: '#4d2e6d',
                        }}
                      >
                        Base Year
                      </span>
                      {copyForAllYears && (
                        <Button
                          label="Copy for All Years"
                          icon="pi pi-copy"
                          size="small"
                          style={{
                            backgroundColor: '#6B21A8',
                            borderColor: '#6B21A8',
                            fontSize: '0.7rem',
                            padding: '4px 10px',
                          }}
                          onClick={() => copyForAllYears()}
                        />
                      )}
                    </td>
                  </tr>
                ) : (
                  <tr
                    style={{
                      background: 'linear-gradient(90deg, #e9d8f5 0%, #f2eafa 100%)',
                      borderTop: '2px solid #c4a8e0',
                    }}
                  >
                    <td colSpan={colCount} style={{ padding: '7px 14px' }}>
                      <span
                        style={{
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: '#6B21A8',
                        }}
                      >
                        {yearLabel}
                      </span>
                    </td>
                  </tr>
                )}

                {/* Data rows for this year */}
                {yearRows.map((packData, rowIdx) => (
                  <tr key={`${yearIndex}-${rowIdx}`}>
                    {NC_COLUMNS.map(c => {
                      const canEdit =
                        isEditMode === true &&
                        NC_EDITABLE_COLS.has(c.key) &&
                        !!onFieldChange &&
                        !!onFieldBlur;

                      if (canEdit) {
                        const fieldName = COL_KEY_TO_HANDLER_FIELD[c.key] ?? c.key;
                        const rawVal = getRawValue(packData, c);
                        return (
                          <td
                            key={c.key}
                            style={{
                              padding: '4px',
                              width: '100px',
                              minWidth: '80px',
                              verticalAlign: 'middle',
                              textAlign: 'center',
                            }}
                          >
                            <InputNumber
                              value={rawVal}
                              onValueChange={e => onFieldChange!(fieldName, e.value)}
                              onFocus={() => {
                                // Pack first: its internal year-reset (NC → 0) runs first.
                                // Year second: correct year wins in React's batch.
                                if (onSetActivePackSizeTab) {
                                  onSetActivePackSizeTab(packData._packKey);
                                }
                                if (onSetActiveYearTab) {
                                  onSetActiveYearTab(packData._yearIndex);
                                }
                              }}
                              onBlur={e => {
                                const target = e.target as HTMLElement;
                                const inputEl =
                                  target.tagName === 'INPUT'
                                    ? (target as HTMLInputElement)
                                    : target.querySelector<HTMLInputElement>('input');
                                if (inputEl) {
                                  const cleaned = inputEl.value.replace(/[$,\s%]/g, '');
                                  const num = parseFloat(cleaned);
                                  if (!isNaN(num)) {
                                    onFieldBlur!(fieldName, num);
                                    return;
                                  }
                                }
                                onFieldBlur!(fieldName, e);
                              }}
                              onKeyDown={e => onFieldKeyDown?.(fieldName, e)}
                              mode="decimal"
                              minFractionDigits={c.type === 'int' ? 0 : 2}
                              maxFractionDigits={c.type === 'int' ? 0 : 2}
                              style={{ width: '100px' }}
                              inputStyle={{
                                fontSize: '0.75rem',
                                padding: '4px 6px',
                                width: '100%',
                              }}
                            />
                          </td>
                        );
                      }

                      return (
                        <td
                          key={c.key}
                          style={NOWRAP_COLS.has(c.key) ? { whiteSpace: 'nowrap' } : undefined}
                        >
                          {getCellValue(packData, c, bidApprovedBy, gsmsHndlgType)}
                        </td>
                      );
                    })}
                  </tr>
                ))}

                {/* Per-year totals row */}
                <tr
                  style={{
                    background: '#f0e8f8',
                    borderTop: '1px solid #c4a8e0',
                    borderBottom: '1px solid #c4a8e0',
                  }}
                >
                  {NC_COLUMNS.map((c, colIdx) => {
                    if (colIdx === 0) {
                      return (
                        <td
                          key={c.key}
                          style={{
                            padding: '0.55rem 0.75rem',
                            textAlign: 'left',
                            color: '#4d2e6d',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                          }}
                        >
                          Subtotal
                        </td>
                      );
                    }
                    if (TOTAL_KEYS.has(c.key)) {
                      const val = yearTotals[c.key];
                      const formatted =
                        c.type === 'currency' ? formatCurrency(val) : formatNum(val);
                      return (
                        <td
                          key={c.key}
                          style={{
                            padding: '0.55rem 0.75rem',
                            fontWeight: 700,
                            color: '#4d2e6d',
                            textAlign: 'center',
                          }}
                        >
                          {formatted}
                        </td>
                      );
                    }
                    return (
                      <td
                        key={c.key}
                        style={{
                          padding: '0.55rem 0.75rem',
                          textAlign: 'center',
                          color: '#9ca3af',
                        }}
                      >
                        —
                      </td>
                    );
                  })}
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
